import React, { useMemo } from 'react';

import { type GlobalShortcutsItem } from '@atlassian/global-shortcuts';
import { useCompanyHub } from '@atlassian/global-shortcuts/company-hub';
import { useJira } from '@atlassian/global-shortcuts/jira';
import { useLoom } from '@atlassian/global-shortcuts/loom';
import { useTeams } from '@atlassian/global-shortcuts/teams';
import type { CustomItemProps } from '@atlassian/navigation-common/types';

import { isTeamsAppEnabled } from '@confluence/teams-app-config/entry-points/isTeamsAppEnabled.ts';

import { CompanyHubItem } from '../apps/CompanyHubItem/CompanyHubItem.tsx';

import { useShortcutVisibilityOverrides } from './useShortcutVisibilityOverrides.tsx';
import { useFallbackUrls } from './useFallbackUrls.tsx';
import { useLicensedProducts } from './useLicensedProducts.tsx';

export type ShortcutItemsConfigProps = {
	teamsShortcutConfig?: GlobalShortcutsItem;
};

// Map shortcut configs to menu identifiers
export const useShortcutItemsConfig = (
	props?: ShortcutItemsConfigProps,
): Record<string, GlobalShortcutsItem> => {
	const fallbackUrls = useFallbackUrls();
	const { hasLoom, hasJira } = useLicensedProducts();
	const teamsAppEnabled = isTeamsAppEnabled();
	const { isPeopleVisible, isCompanyHubVisible } = useShortcutVisibilityOverrides();

	const companyHubShortcutWithoutComponent = useCompanyHub(fallbackUrls['companyHubShortcut']);
	const jiraShortcut = useJira('');
	const loomShortcut = useLoom('');
	const teamsShortcut = useTeams(fallbackUrls['teamsShortcut']);

	return useMemo(() => {
		const companyHubShortcut: GlobalShortcutsItem = {
			...companyHubShortcutWithoutComponent,
			menuItemId: 'companyHubShortcut',
			type: 'component',
			isReorderable: true,
			component: (itemProps: CustomItemProps) => <CompanyHubItem {...itemProps} />,
		};

		return {
			...(isCompanyHubVisible ? { companyHubShortcut } : {}),
			...(hasJira ? { jiraShortcut } : {}),
			...(hasLoom ? { loomShortcut } : {}),
			...(isPeopleVisible && teamsAppEnabled
				? {
						teamsShortcut: props?.teamsShortcutConfig ?? teamsShortcut,
					}
				: {}),
		};
	}, [
		companyHubShortcutWithoutComponent,
		hasJira,
		jiraShortcut,
		hasLoom,
		loomShortcut,
		teamsShortcut,
		props?.teamsShortcutConfig,
		teamsAppEnabled,
		isCompanyHubVisible,
		isPeopleVisible,
	]);
};
